// Application constants for better maintainability
import packageJson from '../../package.json'

// Application version from package.json
export const APP_VERSION = packageJson.version;

// Root folder identifier for backend API - items assigned to this ID are considered root-level
export const ROOT_FOLDER_ID = 'root';

export const FILE_UPLOAD = {
    // Supported file types
    ALLOWED_MIME_TYPES: [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ],

    // Supported file extensions
    ALLOWED_EXTENSIONS: ['.csv', '.xlsx', '.xls'],

    // File size limits (set to effectively unlimited; previously 50MB)
    MAX_FILE_SIZE: Number.MAX_SAFE_INTEGER,

    // Preview rows limit
    PREVIEW_ROWS: 10,

    // Accept attribute for file input
    ACCEPT_ATTRIBUTE: '.csv,.xlsx,.xls'
} as const

export const DATA_TYPES = [
    'Integer',
    'Float',
    'String',
    'Date',
    'Timestamp',
    'Boolean',
    'Complex'
] as const

export type DataType = typeof DATA_TYPES[number]

/**
 * Map of all known source type names (Databricks/Spark, CSV/pandas, legacy)
 * to the canonical frontend DataType category.
 */
const TYPE_ALIAS_MAP: Record<string, DataType> = {
    // Integer aliases
    Int: 'Integer',
    Bigint: 'Integer',
    Smallint: 'Integer',
    Tinyint: 'Integer',
    Long: 'Integer',
    Int64: 'Integer',
    // Float aliases
    Double: 'Float',
    Decimal: 'Float',
    Numeric: 'Float',
    Number: 'Float',
    Real: 'Float',
    Float64: 'Float',
    // String aliases
    Varchar: 'String',
    Char: 'String',
    Text: 'String',
    Object: 'String',
    Json: 'String',
    JSON: 'String',
    // Date aliases (date-only)
    // (no aliases needed – "Date" matches directly)
    // Timestamp aliases
    Datetime: 'Timestamp',
    Time: 'Timestamp',
    Datetime64: 'Timestamp',
    // Boolean aliases
    Bool: 'Boolean',
    // Complex aliases
    Array: 'Complex',
    Map: 'Complex',
    Struct: 'Complex',
    Binary: 'Complex',
}

/**
 * Normalise any raw type string (backend, Databricks, legacy, or lowercase)
 * to a valid frontend DataType category.
 *
 * - Capitalises the first letter (`"integer"` → `"Integer"`).
 * - Maps Databricks/Spark types (`"BIGINT"` → `"Integer"`).
 * - Maps legacy types (`"Number"` → `"Float"`, `"Text"` → `"String"`).
 * - Falls back to `"String"` for unknown values.
 */
export function normalizeDataType(raw: string): DataType {
    if (!raw) return 'String';
    // Capitalise first letter, lowercase remainder
    const capitalised = raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
    // Check if it's already a valid DataType
    if ((DATA_TYPES as readonly string[]).includes(capitalised)) return capitalised as DataType;
    // Check alias map
    if (capitalised in TYPE_ALIAS_MAP) return TYPE_ALIAS_MAP[capitalised];
    return 'String';
}

export const MODAL_CONFIG = {
    Z_INDEX: 9999,
    ANIMATION_DURATION: 300,
    BACKDROP_BLUR: 'backdrop-blur-md'
} as const

export const API_ENDPOINTS = {
    DATA_SOURCES: '/api/datasources',
    UPLOAD: '/api/datasources/upload',
    ANALYZE: '/api/datasources/analyze',
    DELETE: (id: number) => `/api/datasources/${id}`
} as const

export const UI_MESSAGES = {
    UPLOAD: {
        INVALID_FILE_TYPE: 'Please select a CSV or Excel file only.',
        FILE_TOO_LARGE: 'File size exceeds the allowed limit.', // Generic message (won't normally trigger with unlimited setting)
        UPLOAD_FAILED: 'Upload failed. Please try again.',
        ANALYSIS_FAILED: 'Failed to analyze file. Please try again.'
    },
    LOADING: {
        ANALYZING: 'Metadata is being analyzed…',
        UPLOADING: 'Uploading...',
        PROCESSING: 'Please wait while we process your data'
    }
} as const

export const STATUS_COLORS = {
    processing: 'bg-yellow-100 text-yellow-800',
    processed: 'bg-green-100 text-green-800',
    error: 'bg-red-100 text-red-800'
} as const

// Hero image set (modern formats first) used on EntryPage. Centralizing allows future reuse / testing.
export const HERO_IMAGE_SET = 'url(/new_hero.jfif)';

// Centralized AI Disclaimer text (legal / transparency). Keep concise & user-friendly.
// Guardrail: If updated, ensure modal referencing it remains <400 chars primary body for readability.
export const AI_DISCLAIMER_TEXT = `This application uses AI to analyze your data, identify potential data quality issues, and suggest remediation strategies.\n\nImportant:\n• AI outputs may be incomplete, outdated, or contextually imperfect.\n• Always validate critical findings before acting.`;

// Version history: maintain chronological list with feature highlights.
// Add new entries at the top (descending order). Keep concise bullet points (<8 per version).
export interface VersionHistoryEntry {
    version: string
    date: string // Release date (YYYY-MM-DD)
    features: string[] // Business / user value oriented (no low-level tech jargon)
}

// NOTE: Keep entries ordered newest first. Dates approximate unless a formal release calendar exists.
export const VERSION_HISTORY: VersionHistoryEntry[] = [
    {
        version: '1.1',
        date: '03/17/2026',
        features: [
            'Multi-Language Support – Use the app in 9 languages with AI-powered translations powered by Lexi.',
            'Data Quality Rules – Apply ready-to-use validation checks or create your own by describing what you need in plain English.',
            'In-App Feedback – Rate your experience and share comments with one click, right from the page you\'re on.',
            'Schema Validation & Auto-Update – When you replace a file, the app checks compatibility and automatically picks up any column changes.'
        ]
    },
    {
        version: '1.0',
        date: '02/12/2026',
        features: [
            'AI-Powered Diagnosis & Solutions – Automatically finds data issues and recommends how to fix them.',
            'Comprehensive Data Exploration – Bring your own CSV/Excel files or pick tables directly from EDP to start analysing instantly.',
            'Data Health at a Glance – See how your data scores across five key areas: completeness, accuracy, validity, consistency and uniqueness.',
            'Business-Friendly Reports – Clear, visual reports with health scores, issue breakdowns and step-by-step action plans.',
            'Enterprise-Grade Security – Secure sign-in with Azure AD and role-based access through Databricks Unity Catalog.',
            'Smart Data Organization – Keep things tidy with folders — create, move and group your data sources your way.',
            'Historical Reports – Go back to any past analysis anytime to review findings and recommendations.',
            'Data Description & Insights – Get instant, AI-generated summaries of your files and columns so you know what you\'re working with.',
            'Easy Access Requests – Don\'t have access to a table? Request it directly through the built-in Access Pilot link without leaving the app.'
        ]
    }
] as const


// Global Theme System
export const THEME = {
    // Primary action buttons (Create, Add, Save, etc.) - Using custom red color #D31145
    BUTTON_PRIMARY: {
        base: 'text-white',
        hover: 'hover:scale-105 hover:-translate-y-0.5',
        focus: 'focus:ring-2 focus:ring-offset-2',
        disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
        classes: 'px-6 py-3 text-base font-semibold rounded-lg transition-all duration-300 ease-out transform hover:shadow-lg',
        customStyles: {
            border: 'none',
            boxShadow: '0 4px 15px rgba(211, 17, 69, 0.3)',
            background: 'linear-gradient(135deg, #D31145 0%, #B30E38 100%)',
            '--tw-ring-color': '#D31145',
            '--tw-ring-opacity': '0.5'
        },
        hoverStyles: {
            backgroundColor: '#B30E38'
        }
    },

    // Secondary action buttons (Cancel, Back, etc.) - Using custom red color #D31145
    BUTTON_SECONDARY: {
        base: 'text-white',
        hover: 'hover:scale-105 hover:-translate-y-0.5',
        focus: 'focus:ring-2 focus:ring-offset-2',
        disabled: 'disabled:opacity-50 disabled:cursor-not-allowed',
        classes: 'px-6 py-3 text-base font-semibold rounded-lg transition-all duration-300 ease-out transform hover:shadow-lg',
        customStyles: {
            backgroundColor: '#D31145',
            '--tw-ring-color': '#D31145',
            '--tw-ring-opacity': '0.5'
        },
        hoverStyles: {
            backgroundColor: '#B30E38'
        }
    },

    // Text colors based on background
    TEXT: {
        onDark: 'text-white',           // White text on dark background
        onLight: 'text-brand-700',      // Red text on light background
        onLightSecondary: 'text-gray-600', // Gray text on light background
        title: 'text-white',            // Main titles
        subtitle: 'text-gray-400'       // Subtitles
    },

    // Interactive elements
    INTERACTIVE: {
        selected: 'border-brand-700 bg-brand-50', // Selected state
        hover: 'hover:border-brand-700',           // Hover state
        focus: 'focus:ring-2 focus:ring-brand-500' // Focus state
    }
} as const

// Helper function to combine theme classes
export const getButtonClasses = (variant: 'primary' | 'secondary' = 'primary', additional: string = '') => {
    const theme = THEME[`BUTTON_${variant.toUpperCase()}` as keyof typeof THEME] as any
    return `${theme.base} ${theme.hover} ${theme.focus} ${theme.disabled} ${theme.classes} ${additional}`.trim()
}

// Helper function to get button styles (for custom colors)
export const getButtonStyles = (variant: 'primary' | 'secondary' = 'primary') => {
    const theme = THEME[`BUTTON_${variant.toUpperCase()}` as keyof typeof THEME] as any
    return theme.customStyles || {}
}

// Language selector: display name → ISO code + flag emoji mapping.
// Used by useSupportedLanguages to derive available languages from locale file names.
// Add entries here whenever a new locale JSON is added to src/locales/.
export const LANGUAGE_DISPLAY_MAP: Record<string, { code: string; flag: string }> = {
    'English': { code: 'en', flag: '🇺🇸' },
    'Chinese (Simplified)': { code: 'zh-CN', flag: '🇨🇳' },
    'French (Canada)': { code: 'fr-CA', flag: '🇨🇦' },
    'German': { code: 'de', flag: '🇩🇪' },
    'Hindi': { code: 'hi', flag: '🇮🇳' },
    'Italian': { code: 'it', flag: '🇮🇹' },
    'Portuguese': { code: 'pt', flag: '🇵🇹' },
    'Spanish (Latin America)': { code: 'es-LA', flag: '🇪🇸' },
    'Turkish': { code: 'tr', flag: '🇹🇷' },
} as const;



